/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * AI Assistant: who has a seat, and what the organisation's credits went on.
 *
 * Two tabs. Seats hands the seats the organisation bought to users. Usage
 * shows the credit balance by pool, credits by user, by feature and over
 * time, and a per-call history with the short purpose each call recorded.
 *
 * Credits only: no money, and nothing about which AI service answers. Credits
 * are sold by the platform team (admin portal), so the page says how to get
 * more instead of offering a button that cannot work.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
} from "@mui/material";
import { ArrowBack, AutoAwesome, Download, Lock } from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AiAssistantSeats, { displayName } from "components/SettingsComponents/AiAssistantSeats";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_SX = {
  p: 3,
  border: `1px solid ${colors.callCenter.cardBorder}`,
  boxShadow: "none",
  borderRadius: "12px",
};

export const FEATURE_LABELS = {
  chat: "Chat",
  report_gen: "Report drafting",
  dashboard_gen: "Dashboard drafting",
  automation_gen: "Automation drafting",
  lead_scoring: "Lead scoring",
  deal_scoring: "Deal scoring",
  next_best_action: "Next best action",
};

const STATUS_LABELS = { ok: "Completed", error: "Failed", refused: "Refused (no credits)" };
const STATUS_CHIP = {
  ok: colors.status.success,
  error: colors.status.error,
  refused: colors.status.warning,
};

const CHART_COLORS = [
  colors.brand.primary,
  colors.status.success.dot,
  colors.status.warning.dot,
  colors.callCenter.danger,
  colors.status.neutral.dot,
  colors.text.dark,
];

const fmt = (n) => Number(n || 0).toLocaleString();

function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

function defaultRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from: isoDay(from), to: isoDay(to) };
}

function Stat({ label, value, hint }) {
  return (
    <Card sx={{ ...CARD_SX, p: 2.5, height: "100%" }}>
      <MDTypography variant="caption" color="text" textTransform="uppercase" fontWeight="medium">
        {label}
      </MDTypography>
      <MDTypography variant="h4" fontWeight="medium" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </MDTypography>
      {hint && (
        <MDTypography variant="caption" color="text">
          {hint}
        </MDTypography>
      )}
    </Card>
  );
}

function BalanceBanner({ wallet }) {
  if (!wallet) return null;
  if (wallet.hard_stop && wallet.balance <= 0) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Your organisation has no AI Assistant credits left, so the AI Assistant is paused for
        everyone. Ask your PearlCRM account manager to add credits.
      </Alert>
    );
  }
  if (wallet.low_balance) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        AI Assistant credits are running low: {fmt(wallet.balance)} left. Ask your PearlCRM account
        manager to add more before they run out.
      </Alert>
    );
  }
  return null;
}

function UsageTab({ wallet, users }) {
  const { axiosInstance } = useAuth();
  const [filters, setFilters] = useState({ ...defaultRange(), user_id: "", feature: "", status: "", q: "" });
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState({ total: 0, data: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => {
    const p = { from: filters.from || undefined, to: filters.to || undefined };
    if (filters.user_id !== "") p.user_id = filters.user_id;
    if (filters.feature) p.feature = filters.feature;
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance
      .get("/ai-assistant/usage", { params })
      .then((res) => !cancelled && setSummary(res.data))
      .catch((err) => !cancelled && setError(getApiErrorMessage(err, "Could not load usage.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, params]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/ai-assistant/usage/calls", {
        params: { ...params, q: filters.q || undefined, limit: rowsPerPage, offset: page * rowsPerPage },
      })
      .then((res) => !cancelled && setCalls(res.data))
      .catch(() => !cancelled && setCalls({ total: 0, data: [] }));
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, params, filters.q, page, rowsPerPage]);

  const set = (key) => (e) => {
    setPage(0);
    setFilters((f) => ({ ...f, [key]: e.target.value }));
  };

  const exportCsv = async () => {
    try {
      const res = await axiosInstance.get("/ai-assistant/usage/calls", {
        params: { ...params, q: filters.q || undefined, format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `ai-assistant-usage-${filters.from || "all"}-${filters.to || "today"}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not export the usage history."));
    }
  };

  const byFeature = (summary?.by_feature || []).map((r) => ({
    ...r,
    name: FEATURE_LABELS[r.feature] || r.feature,
  }));
  const byUser = (summary?.by_user || []).slice(0, 10);
  const byDay = summary?.by_day || [];

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <Stat label="Credits left" value={fmt(wallet?.balance)}
            hint={wallet?.cycle_resets_at ? `Allowance renews ${new Date(wallet.cycle_resets_at).toLocaleDateString()}` : null} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stat label="Monthly allowance left" value={fmt(wallet?.included_balance)}
            hint={`of ${fmt(wallet?.included_credits_monthly)} a month; unused credits expire`} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stat label="Purchased credits" value={fmt(wallet?.topup_balance)} hint="Never expire" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Stat label="Used in this period" value={fmt(summary?.totals?.credits)}
            hint={`${fmt(summary?.totals?.calls)} requests`} />
        </Grid>
      </Grid>

      <Card sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <MDBox display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <TextField size="small" type="date" label="From" value={filters.from} onChange={set("from")}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="To" value={filters.to} onChange={set("to")}
            InputLabelProps={{ shrink: true }} />
          <TextField size="small" select label="User" value={filters.user_id} onChange={set("user_id")}
            sx={{ minWidth: 180 }}>
            <MenuItem value="">Everyone</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>{displayName(u)}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Feature" value={filters.feature} onChange={set("feature")}
            sx={{ minWidth: 170 }}>
            <MenuItem value="">All features</MenuItem>
            {Object.entries(FEATURE_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" select label="Result" value={filters.status} onChange={set("status")}
            sx={{ minWidth: 150 }}>
            <MenuItem value="">Any result</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </TextField>
          <MDBox flexGrow={1} />
          <MDButton variant="outlined" color="info" size="small" onClick={exportCsv} startIcon={<Download />}>
            Export CSV
          </MDButton>
        </MDBox>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !summary ? (
        <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>
      ) : (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} lg={8}>
            <Card sx={CARD_SX}>
              <MDTypography variant="h6" mb={1}>Credits over time</MDTypography>
              {byDay.length === 0 ? (
                <MDTypography variant="body2" color="text">No usage in this period.</MDTypography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={byDay} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.callCenter.surfaceHover} />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <RTooltip formatter={(v, n) => [fmt(v), n === "credits" ? "Credits" : "Requests"]} />
                    <Line type="monotone" dataKey="credits" stroke={colors.brand.primary} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...CARD_SX, height: "100%" }}>
              <MDTypography variant="h6" mb={1}>By feature</MDTypography>
              {byFeature.length === 0 ? (
                <MDTypography variant="body2" color="text">No usage in this period.</MDTypography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={byFeature} dataKey="credits" nameKey="name" innerRadius={50} outerRadius={85}>
                      {byFeature.map((r, i) => (
                        <Cell key={r.feature} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip formatter={(v) => [fmt(v), "Credits"]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={CARD_SX}>
              <MDTypography variant="h6" mb={1}>By user</MDTypography>
              {byUser.length === 0 ? (
                <MDTypography variant="body2" color="text">No usage in this period.</MDTypography>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(160, byUser.length * 34)}>
                  <BarChart data={byUser} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.callCenter.surfaceHover} />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="user_name" width={150} tick={{ fontSize: 12 }} />
                    <RTooltip formatter={(v) => [fmt(v), "Credits"]} />
                    <Bar dataKey="credits" fill={colors.brand.primary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={CARD_SX}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
          <MDTypography variant="h6">Requests</MDTypography>
          <TextField size="small" placeholder="Search what was asked" value={filters.q} onChange={set("q")}
            sx={{ minWidth: 240 }} inputProps={{ "aria-label": "Search what was asked" }} />
        </MDBox>
        <MDBox sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Feature</TableCell>
                <TableCell>What was asked</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <MDTypography variant="body2" color="text">No requests match these filters.</MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {calls.data.map((c) => {
                const chip = STATUS_CHIP[c.status] || colors.status.neutral;
                return (
                  <TableRow key={c.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(c.created_at).toLocaleString()}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{c.user_name}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{FEATURE_LABELS[c.feature] || c.feature}</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>{c.purpose || "—"}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.credits)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_LABELS[c.status] || c.status}
                        sx={{ backgroundColor: chip.background, color: chip.text, fontWeight: 500 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </MDBox>
        <TablePagination
          component="div"
          count={calls.total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Card>
    </>
  );
}

export default function AiAssistantSettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const [tab, setTab] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");

  const licensed = !entitlementsLoading && isModuleEnabled("AI_ASSISTANT");

  const load = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated || !licensed) return;
    setError("");
    const [walletRes, usersRes] = await Promise.allSettled([
      axiosInstance.get("/ai-assistant/wallet"),
      axiosInstance.get("/users/"),
    ]);
    if (walletRes.status === "fulfilled") setWallet(walletRes.value.data);
    else setError(getApiErrorMessage(walletRes.reason, "Could not load the AI Assistant balance."));
    setUsers(
      usersRes.status === "fulfilled" && Array.isArray(usersRes.value.data)
        ? usersRes.value.data.filter((u) => u.is_active !== false && !u.is_support_user)
        : []
    );
  }, [axiosInstance, isAuthReady, isAuthenticated, licensed]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader title="AI Assistant" summary="Choose who can use the AI Assistant, and see how your organisation's credits are used." />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate("/settings")} aria-label="Back to settings">
            <ArrowBack />
          </IconButton>
          <AutoAwesome sx={{ color: colors.brand.primary, fontSize: 32 }} />
          <MDBox flexGrow={1} ml={0.5}>
            <MDTypography variant="h5" fontWeight="medium">AI Assistant</MDTypography>
            <MDTypography variant="body2" color="text">
              Choose who can use the AI Assistant, and see how your organisation&apos;s credits are used.
            </MDTypography>
          </MDBox>
        </MDBox>
        )}

        {entitlementsLoading && (
          <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>
        )}

        {!entitlementsLoading && !licensed && (
          <Card sx={{ ...CARD_SX, textAlign: "center", py: 6 }}>
            <Lock sx={{ fontSize: 40, color: colors.status.neutral.dot, mx: "auto" }} />
            <MDTypography variant="h6" mt={1}>The AI Assistant is an add-on</MDTypography>
            <MDTypography variant="body2" color="text">
              Ask your PearlCRM account manager to add it to your subscription. It is sold per user,
              and usage is paid for with credits.
            </MDTypography>
          </Card>
        )}

        {licensed && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <BalanceBanner wallet={wallet} />
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, maxWidth: 360 }}>
              <Tab label="Seats" />
              <Tab label="Usage" />
            </Tabs>
            {tab === 0 && <AiAssistantSeats users={users} />}
            {tab === 1 && <UsageTab wallet={wallet} users={users} />}
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
